/* eslint-disable import/no-extraneous-dependencies */
import React, { useState } from 'react';
import {
  Box,
  IconButton,
  useTheme,
  makeStyles,
  useMediaQuery,
} from '@material-ui/core';

import { navigate } from 'gatsby';
import { useLocation } from '@reach/router';
import StyledButton from '../atoms/StyledButton';
import TopNavLink from '../atoms/TopNavLink';
import TopNavLinkWithSubmenu from '../atoms/TopNavLinkWithSubmenu';
import HeaderDrawerMenu from '../molecules/HeaderDrawerMenu';
import HeaderDropdownMenu from '../molecules/HeaderDropdownMenu';
import CreateAccountDrawer from './CreateAccountDrawer';
import logo from '../../static/images/logo.svg';
import logoSmall from '../../static/images/logo-small.svg';
import IconMobileMenu from '../../static/images/icon-mobile-menu.svg';
import DropDownMenuMobile from '../molecules/DropDownMenuMobile';
import { addTagFirebaseAnalytcs, tagsAnalytcs } from '../../analytcs/firebase';

const getVariants = (pathname) => {
  switch (pathname) {
    case '/trocar-contador/':
      return {
        color: '#0CB49D',
        hover: '#09796A',
        disabled: '#BFD9D5',
        buttonMobile: false,
        buttonTextMobile: undefined,
      };
    case '/abrir-empresa/':
      return {
        color: '#2370FF',
        hover: '#0944B2',
        disabled: '#BBC8E0',
        buttonMobile: false,
        buttonTextMobile: undefined,
      };
    case '/quanto-custa/': {
      return {
        color: '#0093F5',
        hover: '#0673BC',
        disabled: '#BFD4E3',
        buttonMobile: false,
        buttonTextMobile: 'ABRIR CNPJ GRÁTIS',
      };
    }
    default:
      return {
        color: '#0093F5',
        hover: '#0673BC',
        disabled: '#BFD4E3',
        buttonMobile: false,
        buttonTextMobile: 'ABRIR EMPRESA GrÁTIS',
      };
  }
};

// Entre 1291 e 1700 px o menu completo não cabia e o "Abrir Empresa Grátis"
// saía da tela. Nessa faixa os espaços do cabeçalho ficam menores.
const compactHeader = '@media screen and (min-width: 1291px) and (max-width: 1700px)';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    position: 'fixed',
    zIndex: 1000,
    top: 0,
    display: 'flex',
    justifyContent: 'center',
  },
  base: {
    height: '75px',
    width: '100%',
    padding: '0 40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.secondary.contrastText,
    boxShadow: '0px 8px 11px rgba(0, 0, 0, 0.05)',
    position: 'relative',

    '&:before': {
      content: 'close-quote',
      width: '100%',
      position: 'absolute',
      margin: '0 auto',
      bottom: 0,

      padding: '1px',
      background: `linear-gradient(90deg, rgba(84,196,179,1) 0%,
      rgba(84,172,223,1) 5%,
      rgba(142,72,152,1) 10%,
      rgba(222,106,46,1) 15%,
      rgba(187,59,74,1) 20%,
      rgba(84,196,179,1) 25%,
      rgba(84,172,223,1) 30%,
      rgba(142,72,152,1) 35%,
      rgba(222,106,46,1) 40%,
      rgba(187,59,74,1) 45%,
      rgba(84,196,179,1) 50%,
      rgba(84,172,223,1) 55%,
      rgba(142,72,152,1) 60%,
      rgba(222,106,46,1) 65%,
      rgba(187,59,74,1) 70%,
      rgba(222,106,46,1) 75%,
      rgba(84,196,179,1) 80%,
      rgba(84,172,223,1) 85%,
      rgba(142,72,152,1) 90%,
      rgba(222,106,46,1) 95%,
      rgba(187,59,74,1) 100%)`,
    },

    '@media screen and (max-width: 1290px)': {
      borderRadius: 0,
      margin: 0,
      maxWidth: '100%',
      padding: '0 30px',

      '&:before': {
        width: '100%',
        margin: '0',
      },
    },

    [compactHeader]: {
      padding: '0 20px',
    },
  },
  items: {
    display: 'grid',
    gridTemplateColumns: 'min-content auto min-content',
    alignItems: 'center',
    maxWidth: '1220px',
    flexBasis: '100%',

    '@media screen and (max-width: 1290px)': {
      justifyContent: 'space-between',
      gridTemplateColumns: 'auto min-content min-content',
    },

    '@media screen and (max-width: 400px)': {
      gridTemplateColumns: 'auto min-content',
    },
  },
  nav: {
    display: 'block',
    margin: '0 30px',
    whiteSpace: 'nowrap',
    '& > span': {
      verticalAlign: 'middle',
    },

    [compactHeader]: {
      margin: '0 12px',
      '& > span': {
        paddingLeft: '12px',
        paddingRight: '12px',
      },
    },
  },
  accountsButton: {
    display: 'flex',
    gap: '10px',
    textAlign: 'end',

    '& .MuiButton-label': {
      fontSize: '12px',
      textTransform: 'inherit',
    },
    '& .MuiButton-text': {
      padding: '10px',
      width: '140px',
    },
  },
  mobileMenuButton: {
    display: 'none',

    '@media screen and (max-width: 1290px)': {
      display: 'block',
    },
  },

  '@media screen and (max-width: 1290px)': {
    nav: { display: 'none' },
  },
  myAccountButton: {
    color: (props) => props.color,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
    // "&&" para ganhar da largura padrão (200px) que o StyledButton aplica
    [compactHeader]: {
      '&&': {
        width: 'auto',
        padding: '6px 10px',
      },
    },
  },
  openCompanyButton: {
    backgroundColor: (props) => props.color,
    '&&': {
      width: '100%',
    },
    [theme.breakpoints.down('sm')]: {
      display: (props) => !props.buttonMobile && 'none',
    },
    [compactHeader]: {
      '&&': {
        width: 'auto',
        padding: '10px 18px',
      },
    },
    '&:hover': {
      backgroundColor: (props) => props.hover,
    },
    '&:disabled': {
      backgroundColor: (props) => props.disabled,
    },
  },
  '@media screen and (max-width: 400px)': {
    accountsButton: { display: 'none' },
  },
}));

const Header = () => {
  const { pathname } = useLocation();

  const variants = getVariants(pathname);

  const theme = useTheme();
  const classes = useStyles(variants);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [accountsMenuAnchor, setAccountsMenuAnchor] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createAccountOpen, setCreateAccountOpen] = useState(false);
  const [createAccountStep, setCreateAccountStep] = useState(0);
  const [openCompanyData, setOpenCompanyData] = useState({});
  const [switchAccountantData, setSwitchAccountantData] = useState({});

  const tagBtnAbrirEmpresa = () => addTagFirebaseAnalytcs(tagsAnalytcs.btn_topo_quanto_abrir_mob);

  return (
    <>
      <header className={classes.root}>
        <Box className={classes.base}>
          <Box className={classes.items}>
            <a href="/" style={{ padding: isMobile ? '12px' : '20px' }}>
              {isMobile && <img src={variants.buttonMobile ? logoSmall : logo} alt="Conube" width={variants.buttonMobile ? '28px' : '136px'} height="28px" />}
              {!isMobile && <img src={logo} alt="Conube" width="136px" height="66px" />}
            </a>

            <nav className={classes.nav}>
              <TopNavLink
                backgroundColor="#c1e6ff"
                textColor="#0093F5"
                to="/trocar-contador/"
                label="Trocar de contador"
              />
              <TopNavLink
                backgroundColor="#c1e6ff"
                textColor="#0093F5"
                to="/quanto-custa/"
                label="Quanto custa?"
              />
              <TopNavLink
                backgroundColor="#c1e6ff"
                textColor="#0093F5"
                to="/solucoes/"
                label="Soluções"
                isNew={false}
              />

              <TopNavLink
                backgroundColor="#c1e6ff"
                textColor="#0093F5"
                to="https://conube.com.br/blog/"
                label="Blog"
              />

              <TopNavLinkWithSubmenu
                backgroundColor="#c1e6ff"
                textColor="#0093F5"
                label="Ferramentas"
                items={[
                  { label: 'Consulta de CNAE', to: 'https://www.cnae-simples.com.br/' },
                  { label: 'Calculadora de Impostos PF x PJ', to: '/calculadora-impostos-clt-pj/' },
                  { label: 'Calculadora Salário CLT x PJ', to: '/calculadora-salario-clt-pj/' },
                ]}
              />

              <TopNavLink
                backgroundColor="#c1e6ff"
                textColor="#0093F5"
                to="/servicos-paralegais/"
                label="Paralegal"
                newTab
              />
            </nav>

            <Box className={classes.accountsButton}>
              <StyledButton
                className={classes.myAccountButton}
                text="Minha conta"
                variant="light"
                color="primary"
                height="50px"
                style={{
                  minWidth: 'max-content',
                }}
                onClick={() => { window.location.href = process.env.GATSBY_DYNAMO_LOGIN; }}
              />

              <StyledButton
                id="btn_topo_quanto_abrir_mob"
                className={classes.openCompanyButton}
                text={isMobile ? variants.buttonTextMobile : 'Abrir Empresa Grátis'}
                variant="contained"
                color="primary"
                height="50px"
                style={{
                  textWrap: 'nowrap',
                }}
                onClick={() => {
                  tagBtnAbrirEmpresa();
                  navigate('/abrir-empresa/');
                }}
              />

              <HeaderDropdownMenu
                menuAnchor={accountsMenuAnchor}
                setMenuAnchor={setAccountsMenuAnchor}
              />
            </Box>

            <IconButton
              onClick={() => setDrawerOpen(true)}
              className={classes.mobileMenuButton}
            >
              <img src={IconMobileMenu} alt="Menu de opções" />
            </IconButton>
          </Box>
          <DropDownMenuMobile open={drawerOpen} setOpen={setDrawerOpen} />

          <CreateAccountDrawer
            open={createAccountOpen}
            setOpen={setCreateAccountOpen}
            process="default"
            step={createAccountStep}
            setStep={setCreateAccountStep}
            openCompanyData={openCompanyData}
            setOpenCompanyData={setOpenCompanyData}
            switchAccountantData={switchAccountantData}
            setSwitchAccountantData={setSwitchAccountantData}
          />
        </Box>
      </header>
    </>
  );
};

export default Header;
